<script setup>
import router from '@/router';

import {onMounted, ref} from 'vue';
import settingsService from '@/services/SettingsService';
import {useRoute} from 'vue-router';
import {getLoggedUser, getToken} from '@/services/AuthService';
import {jwtDecode} from "jwt-decode";
import {useI18n} from 'vue-i18n'

const {t} = useI18n();

const route = useRoute();
const types = [
  {label: t('serviceRequirements.deposit'), value: 'deposit'},
  // {label: 'Výběr', value: 'withdraw'},
  {label: t('serviceRequirements.client_identification'), value: 'client_identification'},
  {label: t('serviceRequirements.message'), value: 'message'}
];
const attachmentInput = ref(null);
const form = ref({type: 'message', state: 'CONFIRMED'});
const usedClient = ref(null);
const formDisabled = ref(false);
// IMA-511: klient ve stavu Inheritance_process/Inheritance_closed/Invalid má jen náhled -
// odeslání by stejně zamítl backend (403) beze změny, tady jen zamykáme tlačítko předem.
const isReadOnly = ref(false);
getLoggedUser().then(user => {
  isReadOnly.value = !!user?.client?.is_read_only;
});
// requirement + logika níže v onMounted() patří k editaci existujícího požadavku podle
// route parametru :id. Route /service-requirements/:id je teď zakomentovaná v router.js
// (viz komentář tam), takže params.id sem nikdy nedorazí a tahle větev se nespustí.
// Ponecháno funkční, kdyby se editace požadavku v budoucnu znovu zaváděla (potřebovala by
// ale ownership kontrolu a zachování historie — viz oprava tichého přepisování "Zprávy").
const requirement = ref(null);

function navigateTo(to) {
  router.push(to);
}

function impersonator() {
  // getToken() vrací token aktivní pro tento panel - u impersonace jde o token
  // ze sessionStorage (localStorage nese pořád jen původní, neimpersonovanou identitu).
  const token = jwtDecode(getToken());
  if (!token || token.impersonator === null) {
    return false
  }
  return token.impersonator;
}

onMounted(async () => {
  const params = route.params;
  const queryParams = route.query;
  if (queryParams.client_id && queryParams.name) {
    usedClient.value = {id: queryParams.client_id, name: queryParams.name};
  }
  if (params.id) {
    getLoggedUser().then(user => {
      if (user.service_requirements) {
        requirement.value = user.service_requirements.find(requirement => requirement.id == params.id);
        if (requirement.value) {
          form.value = requirement.value;
          form.value.state = 'CONFIRMED';
          formDisabled.value = true;
        }
      }
    });
  }
})

const isProcessing = ref(false);
function onSubmit() {

  if(impersonator()) {
    window.alert(t('serviceRequirements.service_request_cannot_be_sent'));
    return;
  }
  if(isReadOnly.value) {
    return;
  }

  isProcessing.value = true;
  if (usedClient.value) {
    form.value.client_id = usedClient.value.id;
  } else {
    delete form.value.client_id;
  }
  settingsService.sendRequirement(form.value).then(() => {
    isProcessing.value = false;
    // Přesměrování zpět — formulář může použít i zprostředkovatel nebo BO
    // v zastoupení klienta, kteří /my-service-requirements přístupné nemají.
    router.back();
  }, error => {
    isProcessing.value = false;
  });
}

function handleFileUpload(e) {
  form.value.attachments = Array.from(e.target.files);
}

function removeClient() {
  usedClient.value = null;
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
        <div class="row">
          <div class="col-3">
            <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('serviceRequirements.back') }}</a>
          </div>
          <div class="col-6 text-center">
            <div class="page-section__title">
              {{ t('serviceRequirements.service_requests') }}
          </div>
        </div>
      </div>
    </div>
    <div class="page-section__content">
      <form @submit.prevent="onSubmit">
        <div class="mb-4">
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body p-4">
                  <div v-if="usedClient" class="row mb-3">
                    <div class="col-12">
                      <div class="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
                        <span>{{ t('serviceRequirements.submit_service_request_by_client2') }} <strong>{{usedClient.name}}</strong>.</span> <button class="btn btn-primary" @click="removeClient" type="button">{{ t('serviceRequirements.sent_by_own_name') }}</button>
                      </div>
                    </div>
                  </div>
                  <div v-if="requirement" class="row mb-3">
                    <div class="col-12">
                      <div class="alert alert-warning cursor-pointer">
                        <div>
                          <h5>{{ t('serviceRequirements.respond_to_service_request') }}</h5>
                        </div>
                        <p class="mb-0">{{requirement.request_text}}</p>
                      </div>
                    </div>
                  </div>


                  <!-- Typ požadavku je dočasně skryt — interně se defaultuje na "message" -->
                  <div v-show="false">
                    <select :disabled="formDisabled" v-model="form.type" class="form-select" id="type">
                      <option :key="index" v-for="(type, index) in types" :value="type.value">{{type.label}}</option>
                    </select>
                  </div>
                  <div class="row mb-3">
                    <div class="col-12">
                      <label for="text" class="form-label">{{ t('serviceRequirements.your_message') }}</label>
                      <textarea v-model="form.text" class="form-control" id="text" rows="3" placeholder="Text zprávy"></textarea>
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-12">
                      <label for="text" class="form-label">{{ t('serviceRequirements.attachments2') }}</label>
                      <input type="file" multiple @change="handleFileUpload" ref="attachmentInput" class="form-control" id="attachments" placeholder="{{ t('serviceRequirements.attachments') }}">
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-12">
                      <small>{{ t('serviceRequirements.your_message_sent_to_backoffice') }}</small>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-12 text-end">
                      <button v-if="!isProcessing" :disabled="impersonator() || isReadOnly" class="btn btn-primary" type="submit">{{ t('serviceRequirements.send_request') }}</button>
                      <button v-else class="btn btn-primary" type="button" disabled><span class="mdi mdi-loading mdi-spin me-2"></span>{{ t('serviceRequirements.processing') }}</button>
                      <div v-if="impersonator()">{{ t('serviceRequirements.cannot_sent_service_request') }}</div>
                      <div v-else-if="isReadOnly">{{ t('serviceRequirements.cannot_sent_service_request_read_only') }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
